import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from './Badge';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

/**
 * QuestionCard — Oblique Design System Component
 * Apple-grade assessment card with fluent spring animations, tactile selection physics,
 * and high-fidelity micro-interactions for LMS MCQ certification engines.
 */
export function QuestionCard({
  id,
  index = 0,
  category = 'General',
  question,
  options = [],
  selectedOption = null,
  onSelectOption,
  isSubmitted = false,
  correctOption = null,
  explanation = '',
  className = ''
}) {
  const isCorrect = isSubmitted && selectedOption === correctOption;

  return (
    <motion.div
      id={id}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04, ease: [0.16, 1, 0.3, 1] }}
      className={`
        bg-[var(--bg-surface)] border rounded-[20px] p-6 sm:p-7 transition-all duration-300 scroll-mt-24
        ${isSubmitted
          ? isCorrect
            ? 'border-emerald-500/40 bg-emerald-500/[0.02] shadow-[0_4px_24px_rgba(16,185,129,0.06)]'
            : 'border-rose-500/40 bg-rose-500/[0.02] shadow-[0_4px_24px_rgba(244,63,94,0.06)]'
          : selectedOption !== null
          ? 'border-[var(--accent)] shadow-[0_4px_20px_rgba(0,0,0,0.04)] ring-1 ring-[var(--accent)]/30'
          : 'border-[var(--border)] hover:border-[var(--border-strong)]'
        }
        ${className}
      `}
    >
      {/* Question Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className="text-[11px] font-bold tracking-wider text-[var(--text-tertiary)] uppercase font-mono px-2 py-0.5 rounded-md bg-[var(--bg-subtle)] border border-[var(--border)]">
            Question {index + 1}
          </span>
          {category && (
            <Badge variant="neutral" size="sm">
              {category}
            </Badge>
          )}
        </div>

        {isSubmitted && (
          <Badge variant={isCorrect ? 'success' : 'danger'} size="sm" dot>
            {isCorrect ? 'Correct (+1 pt)' : 'Incorrect (0 pt)'}
          </Badge>
        )}
      </div>

      {/* Question Title */}
      <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-[var(--text-primary)] mb-6 leading-relaxed tracking-tight">
        {question}
      </h3>

      {/* Options List with Apple Spring Tap & Hover Physics */}
      <div className="space-y-3">
        {options.map((option, optIdx) => {
          const isSelected = selectedOption === optIdx;
          const isTheCorrectOption = isSubmitted && correctOption === optIdx;

          let optionStyle = 'border-[var(--border)] bg-[var(--bg-page)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]';
          
          if (isSubmitted) {
            if (isTheCorrectOption) {
              optionStyle = 'border-emerald-500/60 bg-emerald-500/10 text-[var(--text-primary)] font-medium shadow-[0_2px_12px_rgba(16,185,129,0.12)]';
            } else if (isSelected && !isCorrect) {
              optionStyle = 'border-rose-500/60 bg-rose-500/10 text-rose-700 dark:text-rose-300 shadow-[0_2px_12px_rgba(244,63,94,0.12)]';
            }
          } else if (isSelected) {
            optionStyle = 'border-[var(--accent)] bg-[var(--accent-subtle)]/40 text-[var(--text-primary)] font-medium shadow-[0_2px_14px_rgba(0,0,0,0.06)] ring-1 ring-[var(--accent)]';
          }

          return (
            <motion.button
              type="button"
              key={optIdx}
              onClick={() => !isSubmitted && onSelectOption && onSelectOption(optIdx)}
              disabled={isSubmitted}
              whileHover={!isSubmitted ? { scale: 1.008, x: 2 } : {}}
              whileTap={!isSubmitted ? { scale: 0.985 } : {}}
              transition={{ type: "spring", stiffness: 450, damping: 28 }}
              className={`
                w-full text-left flex items-start gap-3.5 p-4 rounded-[14px] border cursor-pointer select-none transition-colors duration-200 outline-none
                ${optionStyle}
              `}
            >
              {/* Option Letter Indicator */}
              <motion.div 
                animate={{ 
                  scale: isSelected ? 1.08 : 1,
                  rotate: isSelected ? [0, -6, 0] : 0
                }}
                transition={{ type: "spring", stiffness: 400, damping: 22 }}
                className={`
                  w-6 h-6 rounded-full border flex items-center justify-center shrink-0 mt-0.5 text-[11px] font-bold font-mono transition-all duration-200 shadow-xs
                  ${isSubmitted && isTheCorrectOption
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : isSubmitted && isSelected && !isCorrect
                    ? 'bg-rose-500 border-rose-500 text-white'
                    : isSelected
                    ? 'bg-[var(--accent)] border-[var(--accent)] text-white shadow-[0_2px_8px_rgba(0,0,0,0.15)]'
                    : 'border-[var(--border-strong)] text-[var(--text-tertiary)] bg-[var(--bg-surface)]'
                  }
                `}
              >
                {isSelected && !isSubmitted ? (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 25 }}
                    className="flex items-center justify-center"
                  >
                    <ObliqueIcon name="check" size={12} />
                  </motion.span>
                ) : (
                  String.fromCharCode(65 + optIdx)
                )}
              </motion.div>

              {/* Option Text */}
              <span className="text-[13.5px] sm:text-[14.5px] leading-relaxed flex-1">
                {option}
              </span>
            </motion.button>
          );
        })}
      </div>

      {/* Diagnostic Explanation Callout */}
      <AnimatePresence>
        {isSubmitted && explanation && (
          <motion.div 
            initial={{ opacity: 0, height: 0, y: 6 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-5 p-4 bg-[var(--bg-subtle)] rounded-[14px] border border-[var(--border)] text-[12.5px] text-[var(--text-secondary)] leading-relaxed flex items-start gap-2.5 overflow-hidden"
          >
            <div className="w-5 h-5 rounded-md bg-[var(--accent)]/10 text-[var(--accent)] flex items-center justify-center shrink-0 mt-0.5">
              <ObliqueIcon name="lightbulb" size={13} />
            </div>
            <div>
              <strong className="text-[var(--text-primary)] font-semibold">Diagnostic Insight: </strong>
              {explanation}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default QuestionCard;
